import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { FaX } from 'react-icons/fa6'
import { z } from 'zod'

import { Button } from '../../../../components/Button'
import { Input } from '../../../../components/Input'
import { useLocalStorage } from '../../../../hooks/useLocalStorage'
import { usePermission } from '../../../../hooks/usePermission'
import { createStoreGroup, getStoresGroupById, updateStoreGroup } from '../../../../services/storesServices'
import { JWT_TOKEN_KEY_NAME } from '../../../../utils/constants'

interface ModalUpsertStoreGroupProps {
  onClose: () => void
  storeGroupId?: string | null
}

const upsertStoreFormGroupSchema = z.object({
  name: z.string(),
  logo_url: z.string(),
})

type UpsertStoreGroupFormData = z.infer<typeof upsertStoreFormGroupSchema>

export function ModalUpsertStoreGroup({ onClose, storeGroupId = null }: ModalUpsertStoreGroupProps) {
  const [storeGroup, setStoreGroup] = useState<{ id: string, name: string, logo_url: string } | null>(null)
  const [token] = useLocalStorage(JWT_TOKEN_KEY_NAME)

  const { upsertStoreGroup } = usePermission()

  const {
    register,
    handleSubmit,
    setValue,
    formState: {
      isSubmitting,
    },
  } = useForm<UpsertStoreGroupFormData>({
    resolver: zodResolver(upsertStoreFormGroupSchema),
    defaultValues: {
      name: storeGroup?.name,
      logo_url: storeGroup?.logo_url,
    },
  })

  async function handleSubmitUpsertUserForm(data: UpsertStoreGroupFormData) {
    let result
    if (storeGroup?.id) {
      result = await updateStoreGroup({
        id: storeGroup.id,
        name: data.name,
        logo_url: data.logo_url,
      }, token)
    } else {
      result = await createStoreGroup({
        name: data.name,
        logo_url: data.logo_url,
      }, token)
    }

    if (result) {
      onClose()
    }
  }

  const getStoreGroup = useCallback(async () => {
    if (storeGroupId) {
      const { storeGroup: group } = await getStoresGroupById(storeGroupId, token)
      setValue('logo_url', group.logo_url)
      setValue('name', group.name)
      setStoreGroup(group)
    }
  }, [setValue, storeGroupId, token])

  useEffect(() => {
    getStoreGroup()
  }, [getStoreGroup, storeGroupId])

  return (
    <div
      className="fixed top-0 left-0 w-screen h-screen bg-black/65 flex items-center justify-center z-[10000]"
      onClick={onClose}
    >
      <div
        className="flex flex-col bg-slate-100 relative rounded-md p-4 w-[80vw] max-h-[80dvh] md:w-96"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex justify-between mb-4">
          <h1 className="font-semibold text-2xl">
            {storeGroup?.id
              ? 'Editar'
              : 'Criar'} Grupo
          </h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <FaX />
          </Button>
        </header>
        <form
          className="flex flex-col space-y-4 w-full"
          onSubmit={handleSubmit(handleSubmitUpsertUserForm)}
        >
          <div>
            <Input
              placeholder="Nome"
              label="Nome"
              disabled={!upsertStoreGroup}
              required
              {...register('name')}
            />
          </div>
          <div>
            <Input
              placeholder="Url da logo"
              label="Logo"
              disabled={!upsertStoreGroup}
              required
              {...register('logo_url')}
            />
          </div>
          <footer className="ml-auto pt-4">
            <Button
              type="submit"
              size="sm"
              isLoading={isSubmitting}
            >
              Salvar
            </Button>
          </footer>
        </form>
      </div>
    </div>
  )
}
